import React from 'react';

const shimmer = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

const Bone = ({ className = '', style = {} }) => (
  <div
    className={`rounded ${className}`}
    style={{
      background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite ease-in-out',
      ...style,
    }}
  />
);

export const HomepageSkeleton = () => (
  <div className="min-h-screen bg-[#FAFAFA] text-zinc-900 overflow-hidden">
    <style>{shimmer}</style>
    {/* Header */}
    <header className="bg-black/90 border-b border-red-900/20 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bone className="w-12 h-12 rounded-full" />
          <Bone className="w-32 h-6" />
        </div>
        <div className="hidden md:flex items-center space-x-6">
          <Bone className="w-16 h-4" />
          <Bone className="w-20 h-4" />
          <Bone className="w-18 h-4" />
          <Bone className="w-24 h-9 rounded-full" />
          <Bone className="w-24 h-9 rounded-full" />
        </div>
      </div>
    </header>

    {/* Live bar */}
    <div className="bg-red-950/20 py-2 flex justify-center">
      <Bone className="w-56 h-4" />
    </div>

    {/* Hero */}
    <section className="relative py-24 px-4 flex flex-col items-center">
      <Bone className="w-64 h-8 rounded-full mb-10" />
      <Bone className="w-96 h-12 mb-4 max-w-[90vw]" />
      <Bone className="w-80 h-12 mb-8 max-w-[80vw]" />
      <Bone className="w-[28rem] h-5 mb-3 max-w-[85vw]" />
      <Bone className="w-72 h-5 mb-12 max-w-[70vw]" />
      <div className="flex gap-6 flex-wrap justify-center">
        <Bone className="w-56 h-14 rounded-xl" />
        <Bone className="w-56 h-14 rounded-xl" />
      </div>
    </section>

    {/* Stats */}
    <div className="container mx-auto px-4 py-10">
      <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
        {[1, 2, 3].map(i => (
          <div key={i} className="text-center">
            <Bone className="w-20 h-10 mx-auto mb-2" />
            <Bone className="w-24 h-3 mx-auto" />
          </div>
        ))}
      </div>
    </div>

    {/* Ritual Cards */}
    <section className="container mx-auto px-4 py-16">
      <Bone className="w-64 h-8 mx-auto mb-3" />
      <Bone className="w-48 h-4 mx-auto mb-10" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-2xl border border-red-900/20 overflow-hidden">
            <Bone className="w-full h-48" style={{ borderRadius: 0 }} />
            <div className="p-5">
              <Bone className="w-3/4 h-5 mb-3" />
              <Bone className="w-full h-3 mb-2" />
              <Bone className="w-2/3 h-3 mb-4" />
              <div className="flex justify-between items-center">
                <Bone className="w-20 h-6" />
                <Bone className="w-28 h-10 rounded-lg" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

export const CatalogoSkeleton = () => (
  <div className="min-h-screen bg-[#FAFAFA] overflow-hidden">
    <style>{shimmer}</style>
    <div className="text-center pt-10 pb-6 px-4">
      <Bone className="w-56 h-9 mx-auto mb-3" />
      <Bone className="w-36 h-4 mx-auto" />
    </div>
    <div className="flex justify-center items-center gap-6 px-8" style={{ height: 'calc(100vh - 220px)' }}>
      <Bone className="w-16 h-16 rounded-full hidden md:block" style={{ flexShrink: 0 }} />
      <Bone className="w-72 rounded-2xl" style={{ height: '380px', flexShrink: 0 }} />
      <Bone className="w-16 h-16 rounded-full hidden md:block" style={{ flexShrink: 0 }} />
    </div>
  </div>
);

export const LojaSkeleton = () => (
  <div className="min-h-screen bg-[#FAFAFA]">
    <style>{shimmer}</style>
    <header className="bg-black/90 border-b border-red-900/20 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bone className="w-10 h-10 rounded-full" />
          <Bone className="w-28 h-6" />
        </div>
        <Bone className="w-20 h-8 rounded-full" />
      </div>
    </header>
    <div className="container mx-auto px-4 py-12">
      <Bone className="w-48 h-9 mx-auto mb-3" />
      <Bone className="w-64 h-4 mx-auto mb-10" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-2xl border border-red-900/20 overflow-hidden">
            <Bone className="w-full h-56" style={{ borderRadius: 0 }} />
            <div className="p-5">
              <Bone className="w-2/3 h-5 mb-2" />
              <Bone className="w-full h-3 mb-2" />
              <Bone className="w-1/2 h-3 mb-4" />
              <Bone className="w-full h-10 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const AdminSkeleton = () => (
  <div className="min-h-screen bg-white">
    <style>{shimmer}</style>
    <div className="flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-zinc-200 p-4 hidden md:block" style={{ minHeight: '100vh' }}>
        <Bone className="w-32 h-6 mb-8" />
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Bone key={i} className="w-full h-10 rounded-lg mb-2" />
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <Bone className="w-48 h-8" />
          <Bone className="w-24 h-9 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-xl border border-zinc-200 p-6">
              <Bone className="w-24 h-4 mb-3" />
              <Bone className="w-16 h-8 mb-2" />
              <Bone className="w-32 h-3" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-zinc-200 p-6">
          <Bone className="w-36 h-6 mb-6" />
          {[1, 2, 3, 4].map(i => (
            <Bone key={i} className="w-full h-12 rounded-lg mb-3" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const GenericPageSkeleton = () => (
  <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-4">
    <style>{shimmer}</style>
    <Bone className="w-12 h-12 rounded-full mb-6" />
    <Bone className="w-48 h-6 mb-3" />
    <Bone className="w-72 h-4 mb-2" />
    <Bone className="w-56 h-4" />
  </div>
);

export const RitualFormSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50">
    <style>{shimmer}</style>
    <div className="max-w-2xl mx-auto px-4 py-12">
      {/* Progress bar */}
      <div className="flex justify-center gap-2 mb-10">
        {[1, 2, 3].map(i => (
          <Bone key={i} className="w-20 h-2 rounded-full" style={{ background: i === 1 ? 'rgba(147,51,234,0.3)' : 'rgba(0,0,0,0.05)' }} />
        ))}
      </div>
      {/* Card */}
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <Bone className="w-16 h-16 rounded-2xl mx-auto mb-4" style={{ background: 'rgba(147,51,234,0.08)' }} />
        <Bone className="w-56 h-7 mx-auto mb-2" style={{ background: 'rgba(0,0,0,0.06)' }} />
        <Bone className="w-80 h-4 mx-auto mb-8 max-w-full" style={{ background: 'rgba(0,0,0,0.04)' }} />
        <Bone className="w-full h-14 rounded-xl mb-4" style={{ background: 'rgba(0,0,0,0.04)' }} />
        <Bone className="w-full h-14 rounded-xl mb-4" style={{ background: 'rgba(0,0,0,0.04)' }} />
        <Bone className="w-full h-14 rounded-xl mb-6" style={{ background: 'rgba(0,0,0,0.04)' }} />
        <Bone className="w-full h-14 rounded-2xl" style={{ background: 'rgba(147,51,234,0.12)' }} />
      </div>
    </div>
  </div>
);

export const PaymentSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50">
    <style>{shimmer}</style>
    <div className="max-w-lg mx-auto px-4 py-12">
      <Bone className="w-40 h-7 mx-auto mb-8" style={{ background: 'rgba(0,0,0,0.06)' }} />
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <Bone className="w-full h-5 mb-3" style={{ background: 'rgba(0,0,0,0.05)' }} />
        <Bone className="w-3/4 h-5 mb-6" style={{ background: 'rgba(0,0,0,0.05)' }} />
        <Bone className="w-full h-48 rounded-xl mb-6" style={{ background: 'rgba(0,0,0,0.04)' }} />
        <Bone className="w-full h-14 rounded-2xl" style={{ background: 'rgba(147,51,234,0.12)' }} />
      </div>
    </div>
  </div>
);

export default HomepageSkeleton;
